// File: /src/contexts/ThemeContext.js

import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
	// Guarda a preferência do usuário: "system", "light" ou "dark"
	const [savedPreference, setSavedPreference] = useState(() => {
		return localStorage.getItem('theme') || 'system';
	});

	// O tema efetivamente aplicado, que será "dark" ou "light"
	const [appliedTheme, setAppliedTheme] = useState(() => {
		if (savedPreference === 'system') {
			return window.matchMedia('(prefers-color-scheme: dark)').matches
				? 'dark'
				: 'light';
		}
		return savedPreference;
	});

	// Atualiza o tema aplicado sempre que a preferência for "system"
	useEffect(() => {
		if (savedPreference === 'system') {
			const mediaQuery = window.matchMedia(
				'(prefers-color-scheme: dark)',
			);
			const handleChange = (e) => {
				setAppliedTheme(e.matches ? 'dark' : 'light');
			};
			// Define o valor inicial
			setAppliedTheme(mediaQuery.matches ? 'dark' : 'light');
			mediaQuery.addEventListener('change', handleChange);
			return () => mediaQuery.removeEventListener('change', handleChange);
		} else {
			setAppliedTheme(savedPreference);
		}
	}, [savedPreference]);

	// Aplica a classe no body com base no tema efetivamente aplicado
	useEffect(() => {
		document.body.classList.toggle('dark-mode', appliedTheme === 'dark');
		// Dispara um evento se necessário
		window.dispatchEvent(
			new CustomEvent('themeChange', { detail: appliedTheme }),
		);
	}, [appliedTheme]);

	// Função para atualizar a preferência do usuário
	const toggleTheme = useCallback((theme) => {
		setSavedPreference(theme);
		localStorage.setItem('theme', theme);
		// Se o usuário escolher algo diferente de "system", atualize imediatamente o tema aplicado
		if (theme !== 'system') {
			setAppliedTheme(theme);
		}
		// Se for "system", o useEffect acima cuidará de atualizar o tema com base na preferência do OS
	}, []);

	const isDarkMode = useMemo(() => appliedTheme === 'dark', [appliedTheme]);

	const value = useMemo(
		() => ({
			savedPreference,
			appliedTheme,
			isDarkMode,
			toggleTheme,
		}),
		[savedPreference, appliedTheme, isDarkMode, toggleTheme]
	);

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
}
